import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ArquivoService } from '@core/services/arquivo.service';
import { Util } from 'src/app/shared/utils/util';
import { FormBase } from '../../utils/form-base';
import { SweetalertCustom } from '../../utils/sweetalert-custom';
import { ArquivoCreateDto } from './../../../core/dto/arquivo/arquivo-create.dto';

@Component({
  selector: 'app-upload-one-file-v2',
  templateUrl: './upload-one-file-v2.component.html'
})
export class UploadOneFileV2Component extends FormBase implements OnInit {
  @Input() editable: boolean = true;
  @Input() accept = '.pdf';
  @Input() form: FormGroup = new FormGroup({});
  @Input() name = '';
  @Input() canDownload = true;
  @Input() mdButton = false;
  @Input() isEdit: boolean;
  @Input() fileStream = false;
  @Input() validationFileType = 'acceptedTypes';
  @ViewChild('file') inputRef!: any;
  @Input() maxSize = 10; // In Mb
  @Input() acceptedTypes = ['jpeg', 'jpg', 'png', 'pdf'];
  @Input() titulo = `Anexar Arquivo ${this.accept.slice(1)}`
  @Input() messageMaxSize: string;
  @Input() messageAcceptedTypes: string;
  @Input() exibirIconePdf: boolean = false;
  @Output() isEditChanged = new EventEmitter();
  @Output() update = new EventEmitter();
  @Output() onClick = new EventEmitter();
  public file;

  constructor(
    public router: Router,
    public activatedRoute: ActivatedRoute,
    private readonly arquivoService: ArquivoService) {
    super(router, activatedRoute);
    this.choosePageUpdateOrView();
  }

  ngOnInit() {
    this.onFormControlChange();
  }

  get formControl() {
    return this.form?.get(this.name);
  }

  async getBase64FromFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve((reader.result as string)?.split(';base64,').pop() ?? '');
      reader.onerror = () => {
        reject(new Error(`Erro ao ler o arquivo: ${reader.error?.message || 'Erro desconhecido'}`));
      };
    });
  }

  public async onSelectFile(event: Event): Promise<void> {
    if (!this.editable) {
      return;
    }
    this.file = (event.target as HTMLInputElement).files[0];
    this.inputRef.nativeElement.value = '';
    this.form.controls[this.name].setValue(this.file);
    if (this.file) {
      if (!this.isValidFile(this.file)) {
        this.clear();
        return;
      }
      const base64 = await this.getBase64FromFile(this.file);
      const arquivoDto = new ArquivoCreateDto({
        id: this.formControl?.value?.id,
        base64,
        extensao: this.file.type.split('/')[1],
        acao: this.formControl?.value?.id ? 'EDIT' : 'CAD',
        nome: this.file.name.split('.')[0],
      });
      this.formControl?.setValue(arquivoDto);
      this.update.emit();
    }
  }

  isValidFile(file: File): boolean {
    const error = this.getFileValidationError(file);
    if (error) {
      SweetalertCustom.newShowAlertConfirm('warning', 'Atenção!', error);
      return false;
    }

    return true;
  }

  private getFileValidationError(file: File): string {
    let error = '';

    if (this.isFileTooLarge(file)) {
      error = this.getMaxSizeMessage();
    }

    const extension = this.getFileExtension(file);
    const typeError = this.getFileTypeValidationError(extension);
    if (typeError) {
      error = typeError;
    }

    return error;
  }

  private isFileTooLarge(file: File): boolean {
    return file.size > this.maxSize * 1024 * 1024;
  }

  private getMaxSizeMessage(): string {
    return this.messageMaxSize || `O tamanho do arquivo não pode ser maior que ${this.maxSize}MB`;
  }

  private getFileExtension(file: File): string | undefined {
    return `${file.name}`.split('.').pop()?.toLocaleLowerCase();
  }

  private getFileTypeValidationError(extension: string | undefined): string {
    if (!extension) {
      return '';
    }

    const strategies = {
      'accept': {
        isValid: this.accept.includes(extension),
        message: this.messageAcceptedTypes || `O arquivo deve ser do tipo: ${this.accept.toUpperCase()}`
      },
      'acceptedTypes': {
        isValid: this.acceptedTypes.includes(extension),
        message: this.messageAcceptedTypes || `O arquivo deve ser do tipo: ${this.acceptedTypes.join(', ')}`
      }
    } as const;

    const strategy = strategies[this.validationFileType as keyof typeof strategies];
    if (!strategy) {
      return '';
    }

    if (strategy.isValid) {
      return '';
    }

    return strategy.message;
  }

  public clear(): void {
    this.isEdit = false;
    this.formControl.setValue(null);
    this.file = null;
    this.inputRef.nativeElement.value = '';
    this.viewAnexo();
    this.markAsTouched();
    this.onClick.emit();
  }

  public downloadFile(base64: string, fileName: string, fileExtension: string = 'pdf'): void {
    const downlodedFile = Util.base64toBlob(base64, fileExtension);
    const a = document.createElement('a');
    document.body.appendChild(a);
    const url = window.URL.createObjectURL(downlodedFile);
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }


  public downloadFromBack(arquivo: any): void {
    this.arquivoService.download(arquivo.id.toString()).subscribe(res => {
      if (this.fileStream) {
        this.downloadFileByFileStream(res.body, arquivo.nomeOriginal || arquivo.nome);
        return;
      }
      if (res.body.base64) {
        this.downloadFile(res.body.base64, arquivo.nomeOriginal || arquivo.nome);
        return;
      }
      this.downloadFileByFileStream(res.body, arquivo.nomeOriginal || arquivo.nome);
    }, (err) => { });
  }

  public download(): void {
    const arquivo = this.form.controls[this.name].value;
    if (arquivo.id) {
      this.downloadFromBack(arquivo);
      return;
    }
    if (this.fileStream) {
      this.downloadFileByFileStream(arquivo, arquivo.nome);
    }
    this.downloadFile(arquivo.base64, arquivo.nome, arquivo.extensao);
  }

  public viewAnexo(): boolean {
    if (this.file || this.form.get(this.name)?.value) {
      return true;
    }
    return false;
  }

  public downloadFileByFileStream(base64: any, fileName: string): void {
    const downlodedFile = new Blob([base64], { type: 'application/pdf' });
    const a = document.createElement('a');
    document.body.appendChild(a);
    const url = window.URL.createObjectURL(downlodedFile);
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  onFormControlChange() {
    this.formControl.valueChanges.subscribe(value => {
      if (!value && this.file) {
        this.file = null;
        this.clear();
      }
    });
  }

  markAsTouched() {
    this.onClick.emit();
    this.form.get(this.name)?.markAsTouched();
  }

}
